import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useOutletContext, useParams } from 'react-router-dom';
import {
  getGame,
  inputScore,
  portalErrorMessage,
  saveScorecardPhoto,
  scoreGame,
  scorePlayoff,
  startGame,
  startNewSet,
  startPlayoff,
  undoScore
} from '../../features/portal/portalService';
import { clubSectionPath, secretarySectionPath } from './portalPaths';
import { PortalError, PortalLoading, PortalPageHeader } from './portalCommon';
import GameByStatusCardPublicBasic from '../ui/gameStatusCards/gameByStatusCardPublicBasic';
import Scoreboard from '../ui/gameControls/scoreboard';

const gameMeta = (game) => {
  const parts = [];
  if (game.round !== null && game.round !== undefined) parts.push(`Round ${game.round}`);
  if (game.green !== null && game.green !== undefined && game.green !== '') {
    const rink =
      game.rink !== null && game.rink !== undefined && game.rink !== ''
        ? ` / rink ${game.rink}`
        : '';
    parts.push(`Green ${game.green}${rink}`);
  }
  if (game.venue?.name || game.venueName) parts.push(game.venue?.name || game.venueName);
  return parts.join(' • ');
};

const PortalGame = ({ mode }) => {
  const { eventId, venueId, sectionId, gameId } = useParams();
  const { token } = useOutletContext();
  const [game, setGame] = useState(null);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [retry, setRetry] = useState(0);
  const loadSeq = useRef(0);

  const loadGame = useCallback(
    (silent = false) => {
      if (!token || !eventId || !gameId) return undefined;
      let cancelled = false;
      const seq = ++loadSeq.current;

      if (!silent) setLoading(true);
      if (!silent) setError('');

      getGame(token, gameId)
        .then((response) => {
          if (cancelled || seq !== loadSeq.current) return;
          if (response.game) setGame(response.game);
          if (response.event) setEvent(response.event);
        })
        .catch((requestError) => {
          if (!cancelled && !silent && seq === loadSeq.current) {
            setError(portalErrorMessage(requestError));
          }
        })
        .finally(() => {
          if (!cancelled && !silent && seq === loadSeq.current) setLoading(false);
        });

      return () => {
        cancelled = true;
      };
    },
    [token, gameId, eventId]
  );

  useEffect(() => {
    const cleanup = loadGame(false);
    return cleanup;
  }, [loadGame, retry]);

  useEffect(() => {
    const refresh = () => {
      if (!submitting) loadGame(true);
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };

    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [loadGame, submitting]);

  // A read that started before one of this page's writes can finish after it.
  // Bumping the load sequence makes that stale read resolve to a no-op.
  const applyResponse = (data) => {
    if (data?.game) {
      loadSeq.current += 1;
      setGame(data.game);
    }
    if (data?.event) setEvent(data.event);
  };

  const runAction = async (action) => {
    setSubmitting(true);
    setActionError('');
    try {
      const data = await action();
      applyResponse(data);
      return data;
    } catch (requestError) {
      setActionError(portalErrorMessage(requestError));
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  // The manual-score dialogs report their own error, so this one must reject
  // rather than resolve, or the dialog closes as if the score was saved.
  const submitManualScore = async (payload) => {
    setSubmitting(true);
    setActionError('');
    try {
      const data = await inputScore(token, game.id, payload);
      applyResponse(data);
      return data;
    } finally {
      setSubmitting(false);
    }
  };

  const savePhoto = async (image) => {
    const data = await saveScorecardPhoto(token, game.id, image);
    applyResponse(data);
    return data;
  };

  const backTo =
    mode === 'secretary'
      ? secretarySectionPath(eventId, venueId, sectionId)
      : clubSectionPath(eventId, venueId, sectionId);

  const portal = {
    detail: true,
    cardMaxWidth: 700,
    event,
    busy: submitting,
    photoErrorMessage: portalErrorMessage,
    openGame: () => {},
    startGame: (payload) => runAction(() => startGame(token, game.id, payload)),
    submitScore: submitManualScore,
    savePhoto,
    onChanged: () => loadGame(true),
    onUndo: () => runAction(() => undoScore(token, game.id)),
    onNewSet: () => runAction(() => startNewSet(token, game.id)),
    onStartPlayoff: () => runAction(() => startPlayoff(token, game.id)),
    onScore: ({ teamId, scoreValue, multiplierActive }) =>
      runAction(() => scoreGame(token, game.id, { teamId, scoreValue, multiplierActive })),
    onPlayoffScore: ({ teamId }) =>
      runAction(() => scorePlayoff(token, game.id, teamId))
  };

  const headerAction = useMemo(
    () => (
      <Button
        variant="outlined"
        size="small"
        color="inherit"
        startIcon={<RefreshIcon />}
        onClick={() => {
          setRetry((value) => value + 1);
        }}
        disabled={loading || submitting}
        sx={{ whiteSpace: 'nowrap' }}>
        Refresh
      </Button>
    ),
    [loading, submitting]
  );

  if (loading && !game) {
    return (
      <Box>
        <PortalPageHeader title="Game" backTo={backTo} backLabel="Games" />
        <PortalLoading />
      </Box>
    );
  }

  if (!game && error) {
    return (
      <Box>
        <PortalPageHeader title="Game" backTo={backTo} backLabel="Games" />
        <PortalError message={error} onRetry={() => setRetry((value) => value + 1)} />
      </Box>
    );
  }

  if (!game) return null;

  return (
    <Box>
      <PortalPageHeader
        title={game.name || 'Game'}
        subtitle={gameMeta(game)}
        backTo={backTo}
        backLabel="Games"
        barAction={headerAction}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>
          {actionError}
        </Alert>
      )}

      <Box display="flex" justifyContent="center">
        <GameByStatusCardPublicBasic game={game} actions={false} portal={portal} />
      </Box>

      {Boolean(game.sets?.length) && (
        <Box mt={2}>
          <Scoreboard game={game} readOnly />
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={3}>
        If another official scores this game, tap refresh to see their latest changes.
      </Typography>
    </Box>
  );
};

export default PortalGame;
